import {TextareaVariable} from '../types'
import {textareaWithVariables_buildPill} from '../pill/build'
import {textareaWithVariables_normalizePillSpacers} from '../pill/normalize-spacers'


// Factory rather than module-level constant — `/g` regexes carry
// `lastIndex` state across calls, which would break re-entrancy.
//
// Matches any single-level `{...}` literal (no nested braces) — not
// just ALL-CAPS codes. Some callers (e.g. the formula editor) use
// human-readable labels as tokens, e.g. `{Cost, Internal}`.
const tokenRegex = (): RegExp => /\{[^{}]+\}/g


// Append `text` to `frag`, splitting on `\n` and inserting a `<br>` at
// each break. Empty segments (consecutive newlines) skip the text node
// so we don't pollute the DOM with empty text nodes.
function appendTextWithBreaks(frag: DocumentFragment | HTMLElement, text: string) {
    const parts = text.split('\n')
    parts.forEach((part, i) => {
        if (i > 0) frag.appendChild(document.createElement('br'))
        if (part) frag.appendChild(document.createTextNode(part))
    })
}


// Parse a stored value into a DocumentFragment suitable for direct
// insertion into a contenteditable container. Walks left-to-right and
// peels off literal `{TOKEN}` matches against the supplied tokens map:
//   - known tokens   → pill spans
//   - unknown `{…}`  → plain text (so removing a token from config
//                      doesn't silently nuke user content)
//   - `\n`           → `<br>`
//
// Pass an empty `tokens` map to force every `{...}` literal to render
// as plain text — that's how the editor's "source" view mode works.
export function textareaWithVariables_parseValueToDOM(
    value  : string,
    tokens : Map<string, TextareaVariable>,
): DocumentFragment {
    const frag = document.createDocumentFragment()
    if (!value) return frag

    const re = tokenRegex()
    let lastIdx = 0
    let m: RegExpExecArray | null
    while ((m = re.exec(value)) !== null) {
        if (m.index > lastIdx) appendTextWithBreaks(frag, value.slice(lastIdx, m.index))
        const raw = m[0]
        const def = tokens.get(raw)
        if (def) {
            frag.appendChild(textareaWithVariables_buildPill(def))
        } else {
            frag.appendChild(document.createTextNode(raw))
        }
        lastIdx = m.index + raw.length
    }
    if (lastIdx < value.length) appendTextWithBreaks(frag, value.slice(lastIdx))

    textareaWithVariables_normalizePillSpacers(frag)
    return frag
}
